export const en = {
  subtitle: 'Guess the daily country',
  modes: { classic: 'Classic', flag: 'Flag', outline: 'Outline', connections: 'Connections', trivia: 'Trivia', draw: 'Draw' },
  recommendations: {
    title: 'You may also like',
    open: 'Open {name}',
    games: {
      wormillion: 'Daily geography game with rare answers',
      stardewdle: 'Daily Stardew Valley puzzles',
      cascadle: 'Daily cascading word game',
      farhike: 'Daily map challenge with rare country answers',
    },
  },
  draw: {
    subtitle: 'Sketch the outline from memory',
    askLabel: 'Draw this country',
    drawingLabel: 'Drawing',
    askHint: 'From memory — no reference, one attempt.',
    blank: 'a blank sheet — no tracing',
    canvasLabel: 'Drawing canvas',
    start: 'Start drawing',
    undo: 'Undo',
    clear: 'Clear',
    done: 'Done',
    next: 'Next country',
    score: 'Score',
    accuracy: 'Accuracy',
    coverage: 'Coverage',
    deviation: 'Deviation',
    legendTrue: 'True outline',
    legendYours: 'Your line',
    legendOut: 'Too far out',
    legendIn: 'Too far in',
    pts: 'pts',
    learnMore: 'Learn more',
    ranks: [
      'Cartographer Royal',
      'Master Cartographer',
      'Fine Surveyor',
      'Steady Hand',
      'Apprentice',
      'Rough Sketch',
      'Back to the Atlas',
    ],
  },
  trivia: {
    subtitle: 'Ten geography questions a day',
    loading: 'Loading today’s questions…',
    question: 'Question',
    correct: 'Correct!',
    wrong: 'Not quite',
    next: 'Next question',
    seeResult: 'See result',
    xpNote: '3 XP per correct answer, +10 for a perfect ten.',
    comeBack: 'A fresh set of ten at midnight UTC.',
    tiers: ['Flawless — 10 out of 10!', 'Sharp — well played.', 'Not bad. Try again tomorrow.', 'Rough one. The world is big.'],
  },
  conn: {
    subtitle: 'Sort 16 countries into 4 groups',
    oneAway: 'One away…',
    shuffle: 'Shuffle',
    deselect: 'Deselect',
    submit: 'Submit',
    mistakes: 'Mistakes',
    solved: 'Solved!',
    lost: 'Next time!',
    nextPuzzle: 'Next puzzle',
    flawless: 'Flawless — no mistakes!',
    wellPlayed: 'Nicely sorted.',
    lostSub: 'The four groups are revealed above.',
    guesses: 'Guesses',
  },
  soon: 'soon',
  streak: 'Streak',
  today: 'Today',
  yesterday: 'Yesterday',
  nextIn: 'Next country in',
  daily: 'Daily',
  unlimited: 'Unlimited',
  nextCountry: 'Next country',
  skip: 'Skip',
  solved: 'Solved',
  best: 'Best',
  emptyHint: 'Make your first guess to reveal the clues.',
  boardScrollLabel: 'Guess comparison table. Scroll horizontally to see all clues.',
  legend: { correct: 'Match', incorrect: 'No match', numbers: 'answer ↑ higher · ↓ lower', distance: 'closer → farther' },
  placeholder: 'Type a country…',
  guess: 'Guess',
  won: 'Solved!',
  lost: 'Out of guesses',
  answerWas: 'The country was',
  answerCard: { viewProfile: 'View profile', northernHemisphere: 'Northern Hemisphere', southernHemisphere: 'Southern Hemisphere' },
  share: 'Share',
  copyLink: 'Copy link',
  copyResult: 'Copy result',
  copyEmbed: 'Copy embed',
  saveImage: 'Save image',
  shareToday: 'Share today',
  copied: 'Copied!',
  cols: { region: 'Region', pop: 'Pop.', area: 'Area', borders: 'Borders', gdp: 'GDP', temp: 'Temp', dist: 'Distance' },
  colTips: {
    region: "Which continent the country sits on. Green means the same continent as the answer.",
    dist: "Straight-line distance from your guess to the answer. Red is far, green is close; the arrow points toward the answer.",
    borders: "How many countries it shares a land border with. ↑ means the answer has more.",
    temp: "Average yearly temperature, 1991–2020, averaged over the whole country — not the capital.",
    pop: "Total population. ↑ means the answer has more people.",
    area: "Land area in km². ↑ means the answer is bigger.",
    gdp: "GDP per capita in US dollars. ↑ means the answer is richer per head.",
  },
  puzzle: {
    flag: 'Which country has this flag?',
    outline: 'Which country has this shape?',
  },
  account: {
    signIn: 'Sign in',
    withGoogle: 'Continue with Google',
    withDiscord: 'Continue with Discord',
    tagline: 'Save your streak across devices',
    signOut: 'Sign out',
    level: 'Lv',
    viewProfile: 'View profile',
    editProfile: 'Edit profile',
  },
  settings: {
    title: 'Edit Profile',
    avatar: 'Avatar',
    change: 'Change',
    displayName: 'Display name',
    bio: 'Bio',
    bioPh: 'Tell explorers about yourself…',
    birthday: 'Birthday',
    country: 'Country',
    countryNone: 'Not set',
    gender: 'Gender',
    genders: { male: 'Male', female: 'Female', other: 'Other', na: 'Prefer not to say' },
    socials: 'Social links',
    invalidUrl: 'Enter a valid URL for that platform.',
    date: { year: 'Year', month: 'Month', day: 'Day' },
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    signInRequired: 'Sign in to edit your profile.',
    cropTitle: 'Crop avatar',
    cropHint: 'Drag to reposition · scroll to zoom',
    apply: 'Apply',
    cancel: 'Cancel',
    pick: 'Choose image',
    uploadError: 'Upload failed — try a smaller image.',
  },
  netError: 'Network hiccup — try again.',
  offlinePractice: 'Offline practice — not ranked.',
  xp: 'XP',
  levelUp: 'Level up!',
  honors: {
    title: 'Badges & Medals',
    intro: 'Every puzzle you finish is recorded three ways — a rank you carry, a medal you win, and a badge you defend.',
    levelT: 'Level badges',
    levelD: 'XP from daily play carries you through 14 engraved vessels of the journey, from a wayfarer’s staff to a world-sailing flagship.',
    dailyT: 'Daily medals',
    dailyD: 'The Today board ranks every explorer by how many of the six modes they clear, then by how few guesses it took. The top three take gold, silver and bronze — tallied on your profile for good.',
    boardT: 'Board badges',
    boardD: 'Hold a top-three place on the Streak, Unlimited or Level board and every day is engraved on the badge. Gold, silver and bronze days are counted separately.',
    cta: 'See the leaderboard',
  },
  lb: {
    scoreBreakdown: "Score breakdown",
    scoreAvg: "Average",
    scoreNote: "Ranked by modes completed first, then by this average.",
    title: 'Leaderboard',
    today: 'Today',
    streak: 'Streak',
    level: 'Level',
    unlimited: 'Unlimited',
    connections: 'Connections',
    myRank: 'Your rank',
    empty: 'No entries yet — be the first.',
    signInToRank: 'Sign in to appear on the leaderboard.',
    guesses: 'guesses',
    guess1: 'guess',
    totalSolved: 'Solved',
    yourStats: 'Your stats',
    back: 'Back to game',
    nav: 'Leaderboard',
    viewAll: 'View all',
    dailyMedals: 'Daily medals',
    heldBadges: 'Board badges',
    notFound: 'Explorer not found.',
  },
  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated: 28 June 2026',
    sections: [
      {
        h: 'Who we are',
        p: ['GeoDle ("we", "us") is a free daily geography guessing game available at geodle.org.'],
      },
      {
        h: 'What we collect',
        p: [
          'Game data: your guesses, daily progress, streak and experience points — stored to run the game and sync it across your devices.',
          'Account data (only if you sign in with Google or Discord): a unique account identifier, your display name and your avatar image. We never receive your password.',
        ],
      },
      {
        h: 'How we use your data',
        p: [
          'To save and sync your progress, calculate streaks and levels, and show public leaderboards and profiles for signed-in players.',
          'We do not sell your data and we do not use it for advertising.',
        ],
      },
      {
        h: 'Analytics & cookies',
        p: [
          'We use Google Analytics and Microsoft Clarity to understand anonymous, aggregate usage. These services may set their own cookies; we do not use them to identify you personally.',
          'We use one first-party cookie to keep you signed in and sync your progress.',
        ],
      },
      {
        h: 'Your rights & deletion',
        p: [
          'You can permanently delete your account and all associated data at any time using the button below. Deletion takes effect immediately.',
        ],
      },
      { h: 'Contact', p: ['Questions about your data? Email contact@geodle.org.'] },
    ],
    deleteAccount: 'Delete my account & data',
    deleteConfirm: 'This permanently deletes your account and all progress. Continue?',
    deleted: 'Your account and data have been deleted.',
  },
  terms: {
    title: 'Terms of Service',
    updated: 'Last updated: 28 June 2026',
    sections: [
      {
        h: 'Acceptance',
        p: ['By using GeoDle you agree to these terms. If you do not agree, please do not use the service.'],
      },
      {
        h: 'The service',
        p: [
          'GeoDle is a free daily geography guessing game provided "as is" for personal, non-commercial entertainment. We may change, suspend or discontinue any part of it at any time.',
        ],
      },
      {
        h: 'Accounts & fair play',
        p: [
          'Signing in is optional and is used to sync your progress and rank you on the leaderboards. You are responsible for activity on your account.',
          'Do not cheat, automate play, or otherwise manipulate scores, streaks or leaderboards. We may remove entries or accounts that do.',
        ],
      },
      {
        h: 'Acceptable use',
        p: [
          'Choose a display name and avatar that are not offensive, infringing or misleading. We may remove content or accounts that break this rule.',
        ],
      },
      {
        h: 'Intellectual property',
        p: [
          'Country data comes from open datasets (mledoze/countries, Natural Earth). GeoDle is an independent project and is not affiliated with any government or organization.',
        ],
      },
      {
        h: 'Disclaimer & liability',
        p: [
          'The service is provided without warranties of any kind. To the extent permitted by law, we are not liable for any damages arising from your use of GeoDle.',
        ],
      },
      {
        h: 'Changes',
        p: ['We may update these terms from time to time. Continued use after a change means you accept the updated terms.'],
      },
      { h: 'Contact', p: ['Questions? Email contact@geodle.org.'] },
    ],
  },
  country: {
    whereOnEarth: 'Where on Earth',
    atAGlance: 'At a glance',
    howItCompares: 'How it compares',
    neighbours: 'Neighbours',
    inGeoDle: '{name} in GeoDle',
    exploreNearby: 'Explore nearby',
    faqTitle: 'FAQ',
    metaTitle: '{name} — Capital, Population, Area & Facts | GeoDle',
    metaDesc: '{name}: capital {capital}, population {pop}, area {area} km². The {rankPop} most populous country, in {region}. Facts, map, neighbours and more.',
    getToKnow: 'Get to know {name}',
    k: { history: 'Historic moments', people: 'Famous figures', landmarks: 'Must-see landmarks', culture: 'Culture & flavour' },
    sources: 'Sources',
    listTitle: 'Countries',
    listSearch: 'Search countries…',
    listAll: 'All regions',
    listCount: '{n} countries',
    listEmpty: 'No countries match your search.',
    promoTitle: 'Explore every country',
    promoDesc: 'Flags, capitals, populations and an interactive globe — a deep-dive page for all 194 countries.',
    promoCta: 'Browse countries',
    metaTitleList: 'All Countries of the World — Flags, Capitals & Facts | GeoDle',
    metaDescList: 'Browse every country: flags, capitals, population, area and more. Search and filter by region, and explore the world on an interactive globe.',
    play: 'Play',
    guessFlag: 'Guess the flag',
    guessOutline: 'Guess the outline',
    back: 'GeoDle',
    notFound: 'Country not found.',
    landlocked: 'Landlocked',
    coastal: 'Coastal',
    north: 'North',
    south: 'South',
    landBorders: '{n} land borders',
    landBorder: '1 land border',
    noBorders: '{name} has no land borders.',
    locLede: '{name} lies in {subregion}, {region}.',
    locLandlocked: 'It is landlocked, with no sea coast.',
    locCoastal: 'It has a sea coastline.',
    locBorders: 'It shares land borders with {n} countries.',
    locBorder: 'It shares a land border with one country.',
    locIsland: 'As an island nation it has no land borders.',
    f: { capital: 'Capital', population: 'Population', area: 'Area', region: 'Region', subregion: 'Subregion', coast: 'Coast', hemisphere: 'Hemisphere', iso: 'ISO', languages: 'Languages', currency: 'Currency', timezone: 'Time zone', calling: 'Calling', driving: 'Driving', tld: 'TLD', nativeName: 'Native name' },
    s: {
      popWorld: 'most populous country in the world (of {total}).',
      popRegion: 'most populous in {region} (of {total}).',
      areaLargest: 'largest by area',
      biggerThan: 'bigger than {x}',
      smallerThan: 'smaller than {x}',
      perKm2: 'people per km²',
      sparse: 'sparsely populated',
      moderate: 'moderately dense',
      dense: 'densely populated',
      morePeople: 'more people than {x}',
      fewerPeople: 'fewer than {x}',
      vs: 'vs',
      neighbours: 'land neighbours',
      neighbour: 'land neighbour',
    },
    q: {
      capitalQ: 'What is the capital of {name}?',
      capitalA: 'The capital of {name} is {capital}.',
      popQ: 'What is the population of {name}?',
      popA: '{name} has about {pop} people — the {rank} most populous country in the world.',
      areaQ: 'How big is {name}?',
      areaA: '{name} covers {area} km², making it the {rank} largest country by area.',
      regionQ: 'Which region is {name} in?',
      regionA: '{name} is in {subregion}, {region}.',
    },
  },
  footer:
    'Country data: mledoze/countries (ODbL) · Map data: Natural Earth (public domain). GeoDle is an independent project, not affiliated with any government or organization.',
};

export type Dict = typeof en;
